import React, { useEffect, useMemo, useRef, useState } from "react";
import { Marker, useMap } from "react-leaflet";
import L, { LatLngBoundsExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import { Box, Grid, TextField, Typography } from "@mui/material";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import Section from "../../../layout/Section";
import ToggleSwitch from "../../../common/inputs/ToggleSwitch";
import InfoTooltip from "../../../common/overlays/InfoTooltip";
import { areasApi } from "../../../../services/api/areas";
import { parcelsApi } from "../../../../services/api/parcels";
import { generationUnitsApi } from "../../../../services/api/generationUnits";
import { GeoJSON, GenerationUnit } from "../../../../types";
import {
  BaseMap,
  ProjectAreaLayer,
  LandParcelLayer,
  WindTurbinesLayer,
  createWindmillIcon,
} from "../../../map";
import type { ParcelData } from "../../../map";
import { GERMANY_CENTER } from "../../../../utils/geometry";
import { colors } from "../../../../styles/colors";

interface StandortSectionProps {
  projectId: string;
  projectName: string;
  bundesland?: string[];
  landkreis?: string[];
  gemeinde?: string[];
  gewerbesteuerhebesatz: number;
  istSuedregion: boolean;
  canEdit: boolean;
  onFieldChange: (
    field: string,
    value: string | number | boolean | null,
  ) => void;
}

// Höhe der Eingabesteuerelemente (TextField, ToggleSwitch) in dieser Section.
// Reine Anzeigewerte bekommen dieselbe Boxhöhe, damit alle Zeilen im Grid
// gleich hoch sind und der Zeilenabstand überall identisch aussieht.
const VALUE_HEIGHT = 44;

// Statische Beschriftung über dem Feld (wie bei Notion-Properties), analog
// zur MasterDataSection — Label bleibt unabhängig vom Wert klein & stabil.
// forwardRef, damit InfoTooltip (klont das Kind inkl. ref) korrekt andocken kann.
const FieldLabel = React.forwardRef<
  HTMLSpanElement,
  { children: React.ReactNode }
>(function FieldLabel({ children }, ref) {
  return (
    <Typography
      ref={ref}
      variant="caption"
      sx={{ display: "block", color: colors.text.secondary, mb: 0.5 }}
    >
      {children}
    </Typography>
  );
});

// Rahmen ist im Ruhezustand unsichtbar und erscheint erst bei Hover/Focus.
const getFieldSx = (canEdit: boolean) => ({
  "& .MuiOutlinedInput-root": {
    height: VALUE_HEIGHT,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  ...(canEdit && {
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.border.default,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.border.focus,
      borderWidth: 2,
    },
  }),
});

const parseFormattedNumber = (value: string): string =>
  value.replace(/\./g, "").replace(",", ".");

interface NumericFormatAdapterProps {
  inputRef: (instance: HTMLInputElement | null) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  suffix?: string;
}

// Adapter für react-number-format als MUI-TextField-inputComponent (Deutsches Zahlenformat).
const NumericFormatAdapter = React.forwardRef<
  NumericFormatProps,
  NumericFormatAdapterProps
>(function NumericFormatAdapter(props, _ref) {
  const { inputRef, onChange, suffix, ...other } = props;
  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      thousandSeparator="."
      decimalSeparator=","
      allowNegative={false}
      fixedDecimalScale={false}
      suffix={suffix}
      onValueChange={(values) => {
        onChange({ target: { name: props.name, value: values.value } });
      }}
    />
  );
});

function geojsonToLeaflet(geometrie: GeoJSON): [number, number][] {
  let ring: number[][];
  if (geometrie.type === "Polygon") {
    ring = (geometrie.coordinates as number[][][])[0];
  } else {
    ring = (geometrie.coordinates as number[][][][])[0][0];
  }
  return ring.map(([lon, lat]) => [lat, lon] as [number, number]);
}

function BoundsController({
  bounds,
}: {
  bounds: LatLngBoundsExpression | null;
}) {
  const map = useMap();
  const hasFitted = useRef(false);
  useEffect(() => {
    if (bounds && !hasFitted.current) {
      map.fitBounds(bounds, { padding: [24, 24] });
      hasFitted.current = true;
    }
  }, [map, bounds]);
  return null;
}

function ReadOnlyField({
  label,
  values,
}: {
  label: string;
  values?: string[];
}) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Box>
        <FieldLabel>{label}</FieldLabel>
        <Box
          sx={{
            minHeight: VALUE_HEIGHT,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography sx={{ color: colors.text.secondary }}>
            {values && values.length > 0 ? values.join(", ") : "–"}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
}

/**
 * Standort-Section des Projektrahmens.
 * Zeigt die Karte der Projektfläche sowie darunter die automatisch daraus ermittelten
 * Verwaltungsgrenzen (Bundesland, Landkreis, Gemeinde), den Gewerbesteuerhebesatz und
 * die Region (EEG) an. Wird komplett ausgeblendet, solange dem Projekt keine Fläche/kein
 * Standort hinterlegt ist.
 */
const StandortSection: React.FC<StandortSectionProps> = ({
  projectId,
  projectName,
  bundesland,
  landkreis,
  gemeinde,
  gewerbesteuerhebesatz,
  istSuedregion,
  canEdit,
  onFieldChange,
}) => {
  const [coords, setCoords] = useState<[number, number][] | null>(null);
  const [pointLocation, setPointLocation] = useState<[number, number] | null>(
    null,
  );
  const [parcels, setParcels] = useState<ParcelData[]>([]);
  const [anlagen, setAnlagen] = useState<GenerationUnit[]>([]);
  const [loaded, setLoaded] = useState(false);

  const [localGewerbesteuer, setLocalGewerbesteuer] = useState(
    String(gewerbesteuerhebesatz),
  );
  const gewerbesteuerRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setLocalGewerbesteuer(String(gewerbesteuerhebesatz));
  }, [gewerbesteuerhebesatz]);

  const handleGewerbesteuerBlur = () => {
    const raw = gewerbesteuerRef.current?.value ?? localGewerbesteuer;
    const num = parseFloat(parseFormattedNumber(raw.split(" ")[0]));
    onFieldChange("gewerbesteuerhebesatz", isNaN(num) ? 0 : num);
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      areasApi.getProjectArea(projectId),
      parcelsApi.getParcels(projectId),
      generationUnitsApi.getProjectGenerationUnits(projectId),
    ])
      .then(([area, fetchedParcels, fetchedAnlagen]) => {
        if (cancelled) return;
        setCoords(area?.geometrie ? geojsonToLeaflet(area.geometrie) : null);
        setPointLocation(
          area?.breitengrad != null && area?.laengengrad != null
            ? [area.breitengrad, area.laengengrad]
            : null,
        );
        setParcels(fetchedParcels ?? []);
        setAnlagen(fetchedAnlagen ?? []);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const bounds: LatLngBoundsExpression | null = useMemo(() => {
    if (!coords || coords.length === 0) return null;
    return L.latLngBounds(coords).pad(0.15);
  }, [coords]);

  if (!loaded) return null;
  if (!coords && !pointLocation) return null;

  return (
    <Section title="Standort" noPadding>
      <Box
        sx={{
          borderRadius: "16px 16px 0 0",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <BaseMap
          height={280}
          zoom={coords ? 12 : 14}
          center={pointLocation ?? GERMANY_CENTER}
          showZoomControl
        >
          <BoundsController bounds={bounds} />
          {!coords && pointLocation && (
            <Marker position={pointLocation} icon={createWindmillIcon()} />
          )}
          {coords && (
            <>
              <ProjectAreaLayer
                projectName={projectName}
                flaecheCoordinates={coords}
              />
              <LandParcelLayer
                parcels={parcels}
                layerVisibility={{ land_parcels: true }}
              />
              <WindTurbinesLayer
                anlagen={anlagen}
                visible={true}
                flaecheCoordinates={coords}
                readOnly
              />
            </>
          )}
        </BaseMap>
      </Box>

      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <ReadOnlyField label="Bundesland" values={bundesland} />
          <ReadOnlyField label="Landkreis" values={landkreis} />
          <ReadOnlyField label="Gemeinde" values={gemeinde} />

          {/* Gewerbesteuerhebesatz */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box>
              <FieldLabel>Gewerbesteuerhebesatz</FieldLabel>
              <TextField
                value={localGewerbesteuer}
                onChange={(e) => setLocalGewerbesteuer(e.target.value)}
                onBlur={handleGewerbesteuerBlur}
                size="small"
                fullWidth
                sx={getFieldSx(canEdit)}
                inputRef={gewerbesteuerRef}
                slotProps={{
                  input: {
                    readOnly: !canEdit,
                    inputComponent: NumericFormatAdapter as any,
                  },
                  htmlInput: { suffix: " %" },
                }}
              />
            </Box>
          </Grid>

          {/* Region (§ 36h EEG 2023) – Nord/Süd beeinflusst Korrekturfaktor-Capping */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <InfoTooltip title="Nord/Süd-Zuordnung gemäß § 36h Abs. 1 EEG 2023. Beeinflusst die untere Begrenzung der Standortgüte für den Korrekturfaktor. Wird beim Speichern der Fläche/des Standorts aus dem Bundesland vorgeschlagen, kann aber manuell überschrieben werden.">
                <FieldLabel>Region (EEG)</FieldLabel>
              </InfoTooltip>
              <ToggleSwitch
                value={istSuedregion ? "sued" : "nord"}
                options={[
                  { value: "nord", label: "Nord" },
                  { value: "sued", label: "Süd" },
                ]}
                onChange={(val) =>
                  onFieldChange("ist_suedregion", val === "sued")
                }
                disabled={!canEdit}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Section>
  );
};

export default React.memo(StandortSection);
